/**
 * Pure editor operations (spec §4.1 #3): find/replace (plain + regex),
 * merge/split cue. Each takes a {@link SubtitleDocument} and returns a new one,
 * never mutating the input, so the session's undo history can keep prior states.
 * Indices are renumbered after structural edits to stay 1..n contiguous.
 */
import { lineToText, textLine, type Cue, type SubtitleDocument } from '../../core';
import { newUid } from './uid';

function renumber(cues: Cue[]): Cue[] {
  return cues.map((cue, i) => (cue.index === i + 1 ? cue : { ...cue, index: i + 1 }));
}

export interface FindReplaceOptions {
  regex?: boolean;
  caseInsensitive?: boolean;
}

export interface FindReplaceResult {
  doc: SubtitleDocument;
  /** Number of matches replaced across all cue text. */
  count: number;
}

/** Escape a plain (non-regex) search string so it matches literally. */
function escapeForPlain(find: string): string {
  return find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Build the search RegExp; throws on an invalid pattern in regex mode. */
function buildMatcher(find: string, opts: FindReplaceOptions): RegExp {
  const flags = `g${opts.caseInsensitive ? 'i' : ''}`;
  return new RegExp(opts.regex ? find : escapeForPlain(find), flags);
}

/**
 * Whether any line of the cue matches `find` under the given options — the
 * primitive behind the Studio find-as-filter (cue-forge-q51). Uses a fresh
 * non-global RegExp so repeated tests are stateless. Matches against the whole
 * visual line (cue-forge-zxw.7) so a phrase spanning a StyledSpan boundary is
 * found. An empty query matches everything; an invalid regex throws (live
 * callers should guard).
 */
export function cueMatches(cue: Cue, find: string, opts: FindReplaceOptions = {}): boolean {
  if (find === '') return true;
  const re = new RegExp(opts.regex ? find : escapeForPlain(find), opts.caseInsensitive ? 'i' : '');
  return cue.lines.some((line) => re.test(lineToText(line)));
}

/**
 * Replace every match of `find` with `replace` across all cue text, per span so
 * inline styling is preserved. In plain mode `$` in the replacement is literal;
 * in regex mode `$1`/`$&` group references work. Throws on an invalid regex.
 */
export function findReplace(
  doc: SubtitleDocument,
  find: string,
  replace: string,
  opts: FindReplaceOptions = {},
): FindReplaceResult {
  if (find === '') return { doc, count: 0 };
  const matcher = buildMatcher(find, opts);
  const replacement = opts.regex ? replace : replace.replace(/\$/g, '$$$$');
  let count = 0;

  const cues = doc.cues.map((cue) => ({
    ...cue,
    lines: cue.lines.map((line) => ({
      spans: line.spans.map((span) => {
        const found = span.text.match(matcher);
        if (!found) return span;
        count += found.length;
        // String replacement (not a function) so regex group refs like $1 expand.
        return { ...span, text: span.text.replace(matcher, replacement) };
      }),
    })),
  }));

  return { doc: { ...doc, cues }, count };
}

/** Merge the cue at array position `pos` with the next; no-op if there is none. */
export function mergeCues(doc: SubtitleDocument, pos: number): SubtitleDocument {
  if (pos < 0 || pos >= doc.cues.length - 1) return doc;
  const a = doc.cues[pos]!;
  const b = doc.cues[pos + 1]!;
  const merged: Cue = { ...a, startMs: a.startMs, endMs: b.endMs, lines: [...a.lines, ...b.lines] };
  const cues = [...doc.cues.slice(0, pos), merged, ...doc.cues.slice(pos + 2)];
  return { ...doc, cues: renumber(cues) };
}

/**
 * Insert a new empty cue after array position `pos` and renumber 1..n. `pos ===
 * -1` (or below) inserts at the head; `pos` in `[0, cues.length-1]` inserts
 * immediately after that cue; `pos >= cues.length` inserts after the last cue.
 * The new cue starts at the preceding cue's `endMs` (or 0 for a head/empty-doc
 * insert) and runs a default 2s, trimmed down to the next cue's start when a
 * next cue exists and begins strictly later so it fits the gap. An already
 * overlapping neighbourhood keeps the plain 2s span — overlap is QC's job, not a
 * hard error (mirrors timeline {@link createAt}). Returns the new document and
 * the array position the cue landed in.
 */
export function insertCueAfter(
  doc: SubtitleDocument,
  pos: number,
): { doc: SubtitleDocument; index: number } {
  const last = doc.cues.length - 1;
  const p = pos < -1 ? -1 : pos > last ? last : pos; // clamp into [-1, last]
  const at = p + 1; // array position the new cue lands in
  const startMs = p < 0 ? 0 : doc.cues[p]!.endMs;
  const next = doc.cues[at];
  let endMs = startMs + 2000;
  if (next && next.startMs > startMs) endMs = Math.min(endMs, next.startMs);
  endMs = Math.max(endMs, startMs + 1);
  // A fresh cue gets a uid (9ww.7) so durable selection can track it; no id and
  // no sourceIndex — those are import-only fields (mirrors timeline createAt).
  const cue: Cue = {
    index: 0,
    uid: newUid(),
    startMs,
    endMs,
    lines: [{ spans: [{ text: '' }] }],
  };
  const cues = [...doc.cues.slice(0, at), cue, ...doc.cues.slice(at)];
  return { doc: { ...doc, cues: renumber(cues) }, index: at };
}

/** Remove the cue at array position `pos` and renumber; no-op if out of range. */
export function deleteCue(doc: SubtitleDocument, pos: number): SubtitleDocument {
  if (pos < 0 || pos >= doc.cues.length) return doc;
  const cues = [...doc.cues.slice(0, pos), ...doc.cues.slice(pos + 1)];
  return { ...doc, cues: renumber(cues) };
}

/** Split `text` at the whitespace nearest its middle; null if there is none. */
function splitTextMiddle(text: string): [string, string] | null {
  const mid = Math.floor(text.length / 2);
  let best = -1;
  for (let i = 0; i < text.length; i++) {
    if (/\s/.test(text[i]!) && (best === -1 || Math.abs(i - mid) < Math.abs(best - mid))) best = i;
  }
  if (best === -1) return null;
  return [text.slice(0, best).trimEnd(), text.slice(best).trimStart()];
}

/**
 * Split the cue at array position `pos` into two cues. A multi-line cue splits at
 * the line nearest its middle; a single-line cue splits at the whitespace nearest
 * the middle of its text. Duration is divided proportionally to character count.
 * No-op if the cue cannot be split (e.g. a single word).
 */
export function splitCue(doc: SubtitleDocument, pos: number): SubtitleDocument {
  const cue = doc.cues[pos];
  if (!cue) return doc;

  let lines = cue.lines;
  if (lines.length < 2) {
    const split = splitTextMiddle(lineToText(lines[0] ?? textLine('')));
    if (!split) return doc;
    lines = [textLine(split[0]), textLine(split[1])];
  }

  const mid = Math.ceil(lines.length / 2);
  const first = lines.slice(0, mid);
  const second = lines.slice(mid);

  const chars = (ls: typeof lines) => ls.reduce((n, l) => n + lineToText(l).length, 0);
  const firstChars = chars(first);
  const total = firstChars + chars(second);
  const dur = cue.endMs - cue.startMs;
  const at =
    total > 0
      ? cue.startMs + Math.round((dur * firstChars) / total)
      : cue.startMs + Math.round(dur / 2);

  const a: Cue = { ...cue, endMs: at, lines: first };
  // The second piece is a new cue: give it a fresh uid (the spread would
  // otherwise duplicate the original's) so durable selection can tell them apart.
  const b: Cue = {
    ...cue,
    startMs: at,
    endMs: cue.endMs,
    lines: second,
    id: undefined,
    uid: newUid(),
  };
  const cues = [...doc.cues.slice(0, pos), a, b, ...doc.cues.slice(pos + 1)];
  return { ...doc, cues: renumber(cues) };
}
